// Cart submission and notification handling
class CartHandler {
  constructor() {
    this.notification = document.querySelector('.added-to-cart-notification');
    this.cartIndicator = document.querySelector('.products-in-cart');
    this.setupEventListeners();
    this.startCartCheck();
  }

  showNotification() {
    if (!this.notification) {
      console.error('Notification element not found');
      return;
    }

    this.notification.classList.remove('hidden');
    
    setTimeout(() => {
      this.notification.classList.add('hidden');
    }, 3000);
  }

  async checkCartStatus() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (this.cartIndicator) {
        if (cart.item_count > 0) {
          this.cartIndicator.classList.remove('hidden');
        } else {
          this.cartIndicator.classList.add('hidden');
        }
      }
    } catch (error) {
      console.error('Error checking cart status:', error);
    }
  }

  startCartCheck() {
    this.checkCartStatus();    
    setInterval(() => this.checkCartStatus(), 5000);
  }

  async addToCart(formData) {
    try {
      console.log('Sending cart data:', JSON.stringify(formData, null, 2));
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Cart API error:', errorData);
        throw new Error(errorData.description || 'Failed to add item to cart');
      }

      const responseData = await response.json();
      console.log('Cart response:', responseData);

      // Update notification with response data
      if (this.notification) {
        const notificationImage = this.notification.querySelector('img');
        const productNameElement = this.notification.querySelector('.notification-product-title');
        
        if (responseData.items && responseData.items.length > 0) {
          const addedItem = responseData.items[0];
          
          if (notificationImage) {
            // Get the image URL from the variant or product
            const imageUrl = addedItem.image || addedItem.variant_image || addedItem.featured_image?.url;
            if (imageUrl) {
              notificationImage.src = imageUrl;
              notificationImage.alt = addedItem.product_title;
            }
          }
          
          if (productNameElement) {
            productNameElement.textContent = addedItem.product_title;
          }
        }
      }

      this.showNotification();
      // Check cart status immediately after adding item
      this.checkCartStatus();
      return responseData;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  setupEventListeners() {
    document.addEventListener('submit', async (e) => {
      console.log('Form submitted:', e.target);
      if (e.target.matches('form[action="/cart/add"]')) {
        e.preventDefault();
        console.log('Add to cart form submitted');
        
        // Get the variant ID from the hidden input
        const variantIdInput = e.target.querySelector('[name="id"]');
        console.log('Variant ID input:', variantIdInput);
        const sellingPlanSelect = e.target.querySelector('select[name="selling_plan"]');
        console.log('Selling plan select:', sellingPlanSelect);
        
        if (!variantIdInput) {
          console.error('Variant ID input not found');
          return;
        }

        // Get the product ID from the data attribute
        const productId = e.target.dataset.productId;
        console.log('Product ID:', productId);
        if (!productId) {
          console.error('Product ID not found in form data');
          return;
        }
        
        // Get the product JSON data
        const productJSONElement = document.getElementById('ProductJSON-' + productId);
        console.log('Product JSON element:', productJSONElement);
        if (!productJSONElement) {
          console.error('Product JSON element not found');
          return;
        }

        let productJSON;
        try {
          productJSON = JSON.parse(productJSONElement.textContent);
          console.log('Product JSON loaded:', productJSON);
        } catch (error) {
          console.error('Error parsing product JSON:', error);
          return;
        }
        
        // Get selected options based on form type
        const isMainForm = e.target.id === 'main-product-form';
        console.log('Form type:', isMainForm ? 'main form' : 'quick buy form');
        
        const bundleName = isMainForm ? 'main-bundle' : 'bundle';
        const selectedBundle = e.target.querySelector(`input[name="${bundleName}"]:checked`)?.value;
        const selectedColor = e.target.querySelector('input[name="option-color"]:checked')?.value;
        const selectedScent = e.target.querySelector('input[name="option-scent"]:checked')?.value || 
                            e.target.querySelector('select[name="option-scent"]')?.value;
        
        console.log('Selected options:', {
          bundle: selectedBundle,
          color: selectedColor,
          scent: selectedScent
        });
        
        // Try to find the variant ID from the selected options
        let variantId = parseInt(variantIdInput.value, 10);
        console.log('Current variant ID:', variantId);
        
        // If we have product JSON and the variant ID is invalid, try to find the correct variant
        if (productJSON && (isNaN(variantId) || variantId <= 0)) {
          // Create a map of option names to values
          const selectedOptionsMap = {};
          if (selectedBundle) selectedOptionsMap['bundle'] = selectedBundle;
          if (selectedColor) selectedOptionsMap['color'] = selectedColor;
          if (selectedScent) selectedOptionsMap['scent'] = selectedScent;
          
          console.log('Looking for variant with options:', selectedOptionsMap);
          console.log('Product options:', productJSON.options);
          console.log('Available variants:', productJSON.variants.map(v => ({
            id: v.id,
            options: v.options,
            title: v.title
          })));
          
          // Find the variant that matches all selected options
          const variant = productJSON.variants.find(variant => {
            console.log('Checking variant:', {
              id: variant.id,
              options: variant.options,
              title: variant.title
            });
            
            const matches = Object.entries(selectedOptionsMap).every(([optionName, optionValue]) => {
              // Find the option index in the product options
              const optionIndex = productJSON.options.findIndex(opt => 
                opt.toLowerCase() === optionName.toLowerCase()
              );
              
              console.log(`Checking option ${optionName} (${optionValue}) at index ${optionIndex}`);
              
              if (optionIndex === -1) {
                console.log(`Option ${optionName} not found in product options`);
                return false;
              }
              
              // Compare the variant option with the selected value
              const variantOptionValue = variant.options[optionIndex];
              const isMatch = variantOptionValue === optionValue || 
                            variantOptionValue.toLowerCase() === optionValue.toLowerCase() ||
                            variantOptionValue.replace(/\s+/g, '') === optionValue.replace(/\s+/g, '');
              
              console.log(`Option ${optionName} comparison:`, {
                variantValue: variantOptionValue,
                selectedValue: optionValue,
                isMatch
              });
              
              return isMatch;
            });
            
            console.log('Variant match result:', matches);
            return matches;
          });
          
          if (variant) {
            console.log('Found matching variant:', {
              id: variant.id,
              options: variant.options,
              title: variant.title
            });
            variantId = variant.id;
            variantIdInput.value = variantId;
          } else {
            console.error('No matching variant found for selected options:', selectedOptionsMap);
            
            // If no variant found, try to use the first available variant
            if (productJSON.variants && productJSON.variants.length > 0) {
              const firstVariant = productJSON.variants[0];
              console.log('Using first available variant:', firstVariant);
              variantId = firstVariant.id;
              variantIdInput.value = variantId;
            } else {
              console.error('No variants available in product JSON');
              return;
            }
          }
        }
        
        // If we still don't have a valid variant ID, try to get it from the price elements
        if (isNaN(variantId) || variantId <= 0) {
          const priceElements = e.target.querySelectorAll('.price[data-variant-id]');
          if (priceElements.length > 0) {
            // Try to find the price element that corresponds to the selected bundle
            let priceElement = null;
            if (selectedBundle) {
              priceElement = Array.from(priceElements).find(el => {
                const bundleInput = e.target.querySelector(`input[name="${bundleName}"][value="${selectedBundle}"]`);
                return bundleInput && el.closest('label') === bundleInput.nextElementSibling;
              });
            }
            
            // If no matching price element found, use the first one
            if (!priceElement && priceElements.length > 0) {
              priceElement = priceElements[0];
            }
            
            if (priceElement) {
              variantId = parseInt(priceElement.dataset.variantId, 10);
              variantIdInput.value = variantId;
            }
          }
        }
        
        // Final check for valid variant ID
        if (isNaN(variantId) || variantId <= 0) {
         //  console.error('Could not determine a valid variant ID');
          return;
        }
        
        
        const formData = {
          items: [{
            id: variantId,
            quantity: parseInt(e.target.querySelector('select[name="quantity"]')?.value || 1, 10)
          }]
        };

        // Add selling plan if selected and not one-time purchase
        const sellingPlanInput = e.target.querySelector('input[name="selling_plan"]');
        
        if (sellingPlanInput && sellingPlanInput.value) {
          formData.items[0].selling_plan = parseInt(sellingPlanInput.value, 10);
        } else if (sellingPlanSelect && sellingPlanSelect.value && sellingPlanSelect.value !== '') {
          // Fallback: check the select element directly
          formData.items[0].selling_plan = parseInt(sellingPlanSelect.value, 10);
        } else {
        }

        try {
          await this.addToCart(formData);
          // Push variant id to dataLayer after add-to-cart on non-PDP pages
          if (!(document.body && document.body.classList.contains('product'))) {
            try {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: 'variant_id', id: variantId });
            } catch (e) {}
          }
          
          // If this was a quick add form submission, close the quick add modal
          if (!isMainForm) {
            const quickBuyModal = e.target.closest('.quick-buy');
            if (quickBuyModal) {
              quickBuyModal.classList.add('hidden');
            }
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
        }
      }
    });
  }
}

// Initialize cart handler
document.addEventListener('DOMContentLoaded', () => {
  window.cartHandler = new CartHandler();
}); 